import { faSort } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@tosize/ui";
import { DropdownMenu } from "radix-ui";
import { Children, type FC, type ReactElement } from "react";
import styles from "./FinishSelect.module.css";

interface FinishSelectProps extends DropdownMenu.DropdownMenuRadioGroupProps {
	disabled?: boolean;
	children:
		| ReactElement<DropdownMenu.DropdownMenuRadioItemProps>
		| ReactElement<DropdownMenu.DropdownMenuRadioItemProps>[];
}

export const FinishSelect: FC<FinishSelectProps> = ({ children, disabled, ...props }) => {
	const innerValue = Children.map(
		children,
		(child: ReactElement<DropdownMenu.DropdownMenuRadioItemProps>) =>
			child.props.value === props.value ? child.props.children : null,
	);

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild disabled={disabled}>
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need for key event */}
				<div
					onClick={(e) => e.preventDefault()}
					// biome-ignore lint/a11y/noNoninteractiveTabindex: shouldn't be interactive for keyboard users
					tabIndex={0}
					className={cn(
						"flex min-w-[152px] grow appearance-none items-center justify-between space-x-2 rounded border px-2 py-2 focus:outline-none focus-visible:ring-1",
						{
							"border-shades bg-white hover:border-primary-medium focus-visible:border-primary-medium focus-visible:ring-primary-medium/50":
								!disabled,
							"cursor-not-allowed border-shades bg-shades-light": disabled,
						},
					)}
				>
					<span className="flex items-center space-x-2 leading-tight">
						{innerValue.length ? innerValue : "Kies een optie"}
					</span>
					<FontAwesomeIcon icon={faSort} className="text-primary-medium" />
				</div>
			</DropdownMenu.Trigger>

			<DropdownMenu.Portal>
				<DropdownMenu.Content onClick={(e) => e.preventDefault()} loop={true}>
					<DropdownMenu.Arrow
						className="fill-current stroke-1 stroke-[#ccc] text-shades-light"
						width="20"
						height="8"
					/>

					<DropdownMenu.RadioGroup
						className="relative top-[-1px] flex max-w-[17rem] flex-wrap items-center border border-shades bg-shades-light p-1 shadow-md sm:max-w-xs"
						{...props}
					>
						{children}
					</DropdownMenu.RadioGroup>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
};

export const FinishSelectItem: FC<DropdownMenu.DropdownMenuRadioItemProps> = ({
	children,
	...props
}) => {
	return (
		<DropdownMenu.RadioItem
			{...props}
			className={cn(
				"m-1 flex cursor-pointer flex-row space-x-2 rounded border border-transparent bg-shades-light px-2 py-2 text-base text-dark hover:bg-shades hover:shadow-md focus:outline-none focus-visible:bg-shades focus-visible:shadow-md motion-safe:transition sm:px-4 sm:py-2",
				styles.item,
			)}
		>
			{children}
		</DropdownMenu.RadioItem>
	);
};
